import { Policy } from '../page';

interface PolicyViewerProps {
  policy: Policy | null;
  onEdit: (policy: Policy) => void;
}

export default function PolicyViewer({ policy, onEdit }: PolicyViewerProps) {
  if (!policy) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 sticky top-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-text-line text-4xl text-gray-400 w-10 h-10 flex items-center justify-center"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Policy Selected</h3>
          <p className="text-sm text-gray-600">Select a policy from the list to view its details</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700';
      case 'Draft': return 'bg-yellow-100 text-yellow-700';
      case 'Archived': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 sticky top-8 max-h-[calc(100vh-120px)] overflow-y-auto">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex-1">{policy.title}</h2>
          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(policy.status)}`}>
            {policy.status}
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">{policy.description}</p>
        <button
          onClick={() => onEdit(policy)}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
          Edit Policy
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <i className="ri-information-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
            Policy Information
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Category:</span>
              <span className="font-medium text-gray-900">{policy.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Version:</span>
              <span className="font-medium text-gray-900">v{policy.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Effective Date:</span>
              <span className="font-medium text-gray-900">
                {new Date(policy.effectiveDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated:</span>
              <span className="font-medium text-gray-900">
                {new Date(policy.lastUpdated).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Updated By:</span>
              <span className="font-medium text-gray-900">{policy.updatedBy}</span>
            </div>
          </div>
        </div>

        {policy.tags.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <i className="ri-price-tag-3-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {policy.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-file-text-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
            Policy Content
          </h3>
          <div className="prose prose-sm max-w-none">
            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {policy.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
